import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import {
  patchGenerarDeclaracion,
  postDeclaracionNomina,
  // postRechazarDeclaracion,
} from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import moment from 'moment';
import ModalConfirm from './ModalConfirm';

const contribuyentesId = [
  26533,
  16766,
  23903,
  609,
  27420,
  10433,
  410,
  26611,
  18658,
  608,
  24805,
  21976,
  2420,
  30942,
  30943,
  30944,
  30945,
  30946,
  30947,
  30948,
  46535,
  49061,
];

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),

  numeric: [
    {
      validator: async (_, value) => {
        if (!value) return Promise.resolve();

        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        const esNumero = !Number.isNaN(parseFloat(value));

        if (!esNumero || !Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }

        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }

        return Promise.resolve();
      },
    },
  ],

  mayorCero: [
    {
      validator: async (_, value) => {
        if (!value) return Promise.resolve();

        const valorNumerico = parseFloat(value);
        if (Number.isNaN(valorNumerico) || valorNumerico <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
        return Promise.resolve();
      },
    },
  ],

  mayorIgualCero: [
    {
      validator: async (_, value) => {
        if (!value) return Promise.resolve();

        const valorNumerico = parseFloat(value);
        if (Number.isNaN(valorNumerico) || valorNumerico < 0) {
          throw new Error('El valor debe ser mayor o igual a 0.');
        }
        return Promise.resolve();
      },
    },
  ],

  entero: [
    {
      validator: async (_, value) => {
        if (!value) return Promise.resolve();

        const regex = /^[0-9]+$/;
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }

        return Promise.resolve();
      },
    },
  ],

  rfc: [
    {
      pattern: RFC_PATTERN,
      message: 'Ingrese un RFC válido.',
    },
  ],

  curp: [
    {
      pattern: CURP_PATTERN,
      message: 'Ingrese una CURP válida.',
    },
  ],
};

const initialValues = {
  es_declaracion_en_ceros: false,
  bases_exentas: 0,
  bases_gravadas: 0,
  retencion: 0,
  actualizacion: 0,
  recargos: 0,
  multas: 0,
  cantidad_a_cargo: 0,
  cantidad_a_favor: 0,
  total_de_contribuciones: 0,
  impuesto: 0,
  impuesto_determinado: 0,
  monto_pagado_con_anterioridad: 0,
};

const FormNomina = ({
  padron,
  statusDeclaracion,
  visible,
  selectedCargos,
  setVisibleForm,
  isComplementaria,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [porcentajePeriodo, setPorcentajePeriodo] = useState(0);
  const [INPC, setINPC] = useState(0);

  // watchers
  // const baseGravada = Form.useWatch(['bases_gravadas'], form);
  const importeDeActualizacion = Form.useWatch(['actualizacion'], form);
  const impuestoDeterminado = Form.useWatch(['impuesto_determinado'], form); // A
  const actualizacion = Form.useWatch(['actualizacion'], form); // B
  const recargos = Form.useWatch(['recargos'], form); // C
  const multas = Form.useWatch(['multas'], form); // D
  const totalDeContribuciones = Form.useWatch(['total_de_contribuciones'], form); // E
  const montoPagadoConAnterioridad = Form.useWatch(['monto_pagado_con_anterioridad'], form); // G

  /* const handleCancel = async () => {
    setLoading(true);
    await postRechazarDeclaracion({
      declaracion: declaracionId,
      canal_de_pago: 5,
    });
    setLoading(false);
  };

  const handleBeforeUnload = () => {
    handleCancel();
  };

  const handleUnload = (event) => {
    const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = message; // Esto mostrará el cuadro de confirmación
    return message;
  }; */

  const calcularRecargos = async ({ importe }) => {
    try {
      // actualizacion
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ recargos: 0 });
        return;
      }

      const _impuestoDeterminado = typeof impuestoDeterminado === 'number'
        ? impuestoDeterminado
        : parseFloat(impuestoDeterminado);
      const fechaVencimientoMasUno = moment(selectedCargos[0]?.fecha_de_vencimiento)
        .date(moment(selectedCargos[0]?.fecha_de_vencimiento).date() + 1)
        .format('YYYY-MM-DD');
      const _recargos = await recargosTotal({
        tipo_de_cargo: selectedCargos[0],
        importe_cargo: importeNumerico,
        base_gravada: _impuestoDeterminado,
        fecha_de_vencimiento: fechaVencimientoMasUno,
      });

      form.setFieldsValue({ recargos: round(_recargos || 0)[0] });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ recargos: 0 });
    }
  };

  const calcularActualizacion = async ({ importe }) => {
    try {
      const importeNumerico = typeof importe === 'number' ? importe : parseFloat(importe);

      if (Number.isNaN(importeNumerico)) {
        form.setFieldsValue({ actualizacion: 0 });
        return;
      }

      const _actualizacion = await actualizacionTotal({
        importe_cargo: importeNumerico,
        tipo_de_cargo: selectedCargos[0],
        INPC,
      });

      form.setFieldsValue({
        actualizacion: round(_actualizacion || 0)[0],
      });
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ actualizacion: 0 });
    }
  };

  // Calculamos la actualización
  useEffect(() => {
    calcularActualizacion({ importe: impuestoDeterminado });
  }, [impuestoDeterminado]);

  // Calculamos los recargos
  useEffect(() => {
    calcularRecargos({ importe: importeDeActualizacion });
  }, [importeDeActualizacion, impuestoDeterminado]);

  const calcularTotalContribuciones = () => {
    try {
      const valores = [
        impuestoDeterminado,
        actualizacion,
        recargos,
        multas,
      ].map((valor) => {
        const valorNumerico = typeof valor === 'number' ? valor : parseFloat(valor);
        return Number.isNaN(valorNumerico) ? 0 : valorNumerico;
      });

      const total = valores.reduce((suma, valor) => suma + valor, 0);

      if (total > 0) {
        form.setFieldsValue({ total_de_contribuciones: round(total)[0] });
      }
    } catch (error) {
      _logger(error);
      form.setFieldsValue({ total_de_contribuciones: 0 });
    }
  };

  // Calculamos Total de contribuciones
  useEffect(() => {
    calcularTotalContribuciones();
  }, [
    impuestoDeterminado,
    actualizacion,
    recargos,
    multas,
  ]);

  const calcularCantidadACargo = () => {
    try {
      const totalContribuciones = typeof totalDeContribuciones === 'number'
        ? totalDeContribuciones
        : parseFloat(totalDeContribuciones) || 0;

      const montoPagado = typeof montoPagadoConAnterioridad === 'number'
        ? montoPagadoConAnterioridad
        : parseFloat(montoPagadoConAnterioridad) || 0;

      const diferencia = totalContribuciones - montoPagado;

      if (diferencia > 0) {
        form.setFieldsValue({
          cantidad_a_cargo: round(diferencia)[0],
          cantidad_a_favor: 0,
        });
      } else {
        form.setFieldsValue({
          cantidad_a_favor: round(Math.abs(diferencia))[0],
          cantidad_a_cargo: 0,
        });
      }
    } catch (error) {
      _logger(error);
      form.setFieldsValue({
        cantidad_a_cargo: 0,
        cantidad_a_favor: 0,
      });
    }
  };

  // Calculamos cantidad_a_cargo (E - G)
  useEffect(() => {
    calcularCantidadACargo();
  }, [totalDeContribuciones, montoPagadoConAnterioridad]);

  const calculateINPC = async () => {
    if (!selectedCargos.length || !selectedCargos[0]) return;

    const periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
    const mes = selectedCargos[0]?.identificador_periodo;
    const _inpc = await getINPC(periodo, mes);
    setINPC(_inpc?.[0] ?? {});
  };

  useEffect(() => {
    if (visible) {
      calculateINPC();
    }

    if (visible) {
      const handleBeforeUnload = (event) => {
        const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = message;
        return message;
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }

    return () => {};
  }, [visible]);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      const _periodo = selectedCargos[0]?.periodo_fiscal?.periodo || '2024';
      let _porcentaje = (selectedCargos[0]?.importe || 5) / 100;

      if (padron?.id && contribuyentesId.includes(padron?.id)
      && (_periodo === 2025 || _periodo === 2026)) {
        _porcentaje = 0.02;
      }
      setPorcentajePeriodo(_porcentaje);
      form.resetFields();
    }
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada
          && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status
          .descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status
          .descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }

      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
        numero_de_empleados: values.numero_de_empleados,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo:
          padron?.empresa?.contribuyente?.razon_social
          || padron?.empresa?.contribuyente?.nombre_completo
          || `${padron?.empresa?.contribuyente?.nombre ?? ''} ${padron?.empresa?.contribuyente?.apellido_paterno ?? ''} ${padron?.empresa?.contribuyente?.apellido_materno ?? ''}`.trim()
          || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno:
          padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno:
          padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        numero_de_empleados:
          values.numero_de_empleados === '' ? 0 : values.numero_de_empleados,
        entidad: 1,
        fecha_del_pago_realizado_anterior:
          isComplementaria && !isZeroState
            ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT)
            : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          bases_exentas: 0,
          bases_gravadas: 0,
          actualizacion: 0,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto_determinado: 0,
          base_de_impuesto: 0,
          monto_pagado_con_anterioridad: 0,
          multas: 0,
          recargos: 0,
          retencion: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionNomina(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>IMPUESTO SOBRE NÓMINAS</Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text>
                  Cancelar
                </Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      setIsVisibleModal(true);
                    })
                    .catch(() => {
                      setIsVisibleModal(false);
                    });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>
                  Generar
                </Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={initialValues}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  hasFeedback
                  name="numero_de_empleados"
                  rules={[..._rules.numeric]}
                  label="Total de empleados"
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Base exenta</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="bases_exentas"
                              tooltip="Fundamento: Artículo 89, ley de hacienda del estado de Nayarit vigente."
                              rules={[
                                _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                ..._rules.numeric,
                                ..._rules.mayorIgualCero,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>Base gravada</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="bases_gravadas"
                              tooltip="Fundamento: Artículo 84, ley de hacienda del estado de Nayarit vigente."
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.numeric,
                                ..._rules.mayorCero,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({
                                      impuesto_determinado: 1 * porcentajePeriodo,
                                    });
                                  } else {
                                    const val_monto = round((parseFloat(value.target.value)
                                    * porcentajePeriodo) || 0)[0];
                                    form.setFieldsValue({
                                      impuesto_determinado: val_monto,
                                    });
                                  }
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>A. Impuesto determinado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_determinado"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>B. Actualización</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[
                                _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>C. Recargos</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[
                                _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>D. Multas</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multas"
                              rules={[
                                _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>E. Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="(A + B + C + D)"
                              name="total_de_contribuciones"
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.numeric,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>F. Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[
                                    _rules.required('El campo es requerido'),
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>G. Monto pagado con anterioridad</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                    ..._rules.numeric,
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input prefix="$" />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>H. Cantidad a cargo</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_cargo"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>I. Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormNomina.propTypes = {
  padron: propTypes.shape({
    id: propTypes.number,
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  selectedCargos: propTypes.arrayOf(
    propTypes.shape({
      periodo_fiscal: propTypes.shape({
        periodo: propTypes.string,
      }),
      identificador_periodo: propTypes.string,
      fecha_de_vencimiento: propTypes.string,
      importe: propTypes.number,
    }),
  ),
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  setVisibleForm: propTypes.func.isRequired,
  callback: propTypes.func.isRequired,
  declaracionId: propTypes.number.isRequired,
  callbackCancel: propTypes.func.isRequired,
};

FormNomina.defaultProps = {
  isComplementaria: false,
  visible: false,
  selectedCargos: [],
};

export default FormNomina;
